import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Language as LanguageIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  ShoppingCart as ShoppingCartIcon,
  Article as ArticleIcon,
  Gavel as GavelIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { GET_SITE_SETTINGS } from '../graphql/queries';
import { UPDATE_SITE_SETTINGS } from '../graphql/mutations';

const SiteSettingsTab = () => {
  const [formData, setFormData] = useState({
    siteName: '',
    siteDomain: '',
    termsUrl: '',
    blogUrl: '',
    shopUrl: '',
    serviceDescription: '',
    servicePrice: '',
    priceCurrency: 'BGN',
    showBuyButton: false,
    showTermsLink: false,
    showBlogLink: false,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const { data, loading, error, refetch } = useQuery(GET_SITE_SETTINGS);
  const [updateSettings, { loading: updateLoading }] = useMutation(UPDATE_SITE_SETTINGS);

  useEffect(() => {
    if (data?.siteSettings) {
      setFormData({
        siteName: data.siteSettings.siteName || '',
        siteDomain: data.siteSettings.siteDomain || '',
        termsUrl: data.siteSettings.termsUrl || '',
        blogUrl: data.siteSettings.blogUrl || '',
        shopUrl: data.siteSettings.shopUrl || '',
        serviceDescription: data.siteSettings.serviceDescription || '',
        servicePrice: data.siteSettings.servicePrice || '',
        priceCurrency: data.siteSettings.priceCurrency || 'BGN',
        showBuyButton: data.siteSettings.showBuyButton || false,
        showTermsLink: data.siteSettings.showTermsLink || false,
        showBlogLink: data.siteSettings.showBlogLink || false,
      });
    }
  }, [data]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      await updateSettings({
        variables: {
          input: {
            siteName: formData.siteName,
            siteDomain: formData.siteDomain,
            termsUrl: formData.termsUrl,
            blogUrl: formData.blogUrl,
            shopUrl: formData.shopUrl,
            serviceDescription: formData.serviceDescription,
            servicePrice: formData.servicePrice,
            priceCurrency: formData.priceCurrency,
            showBuyButton: formData.showBuyButton,
            showTermsLink: formData.showTermsLink,
            showBlogLink: formData.showBlogLink,
          }
        }
      });

      setSuccessMessage('Настройките са запазени успешно!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setErrorMessage('Грешка при запазване: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Грешка при зареждане: {error.message}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LanguageIcon color="primary" />
        Настройки на сайта
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Конфигурирайте основните настройки на сайта, линкове и информация за услугата
      </Typography>

      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      <Grid container spacing={3}>
        {/* Основни настройки */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Основни настройки
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Име на сайта"
                    value={formData.siteName}
                    onChange={handleChange('siteName')}
                    placeholder="Bash Inv"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Домейн на сайта"
                    value={formData.siteDomain}
                    onChange={handleChange('siteDomain')}
                    placeholder="inv.cyberbuch.org"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Използва се за генериране на линкове към документи"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Описание на услугата */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Описание на услугата
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Описание"
                    value={formData.serviceDescription}
                    onChange={handleChange('serviceDescription')}
                    placeholder="Електронно фактуриране за малкия и среден бизнес..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Цена"
                    value={formData.servicePrice}
                    onChange={handleChange('servicePrice')}
                    placeholder="29.99"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Валута"
                    value={formData.priceCurrency}
                    onChange={handleChange('priceCurrency')}
                    placeholder="BGN"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Външни линкове */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Външни линкове
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Общи условия (URL)"
                    value={formData.termsUrl}
                    onChange={handleChange('termsUrl')}
                    placeholder="https://example.com/terms"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GavelIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showTermsLink}
                        onChange={handleChange('showTermsLink')}
                      />
                    }
                    label="Покажи в менюто"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Блог / Помощ (URL)"
                    value={formData.blogUrl}
                    onChange={handleChange('blogUrl')}
                    placeholder="https://blog.example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ArticleIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Линк към блог система с инструкции и помощ"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showBlogLink}
                        onChange={handleChange('showBlogLink')}
                      />
                    }
                    label="Покажи в менюто"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Магазин / WooCommerce (URL)"
                    value={formData.shopUrl}
                    onChange={handleChange('shopUrl')}
                    placeholder="https://shop.example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ShoppingCartIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Линк към WooCommerce за закупуване на абонамент"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.showBuyButton}
                        onChange={handleChange('showBuyButton')}
                      />
                    }
                    label="Покажи бутон 'Купи' в менюто"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Бутон за запазване */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={updateLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={updateLoading}
            >
              {updateLoading ? 'Запазване...' : 'Запази настройките'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SiteSettingsTab;
